import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../iba-components/Button'
import { Input } from '../../../iba-components/Input'
import { Modal } from '../../../iba-components/Modal'
import { Table } from '../../../iba-components/Table'
import { showPopBanner } from '../../../iba-components/PopBanner'
import { getBaseThemeColumnList, getClientThemeColumnList, getDuplicateCheckColumnList } from './tableColumns'
import {
    fetchAllThemes,
    fetchBaseThemes,
    insertBaseThemeMap,
    updateBaseThemeMap,
    insertClientThemeMap,
    updateClientThemeMap,
    duplicationsThemeCheck
} from '../../api/theme'
import { Loading } from '../Loading'
import LoadingMask from '../LoadingMask'
import { BASE_THEME_TYPE } from '../../pages/BaseTheme/baseTheme'
import { CLIENT_THEME_TYPE } from '../../pages/ClientTheme/clientTheme'
import styles from './index.module.css'

const DuplicateCheckResultModal = ({ showModal, handleCancel, selection }) => {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [resultList, setResultList] = useState([])

    useEffect(() => {
        const generateResult = async () => {
            setLoading(true)
            const res = await duplicationsThemeCheck({
                baseThemeMap: selection
            })
            setLoading(false)
            if (res && !res.hasError) {
                res.data.forEach((lineItem) => {
                    const baseThemes = []
                    lineItem.base_theme_map.forEach((baseThemeMapItem) => {
                        baseThemes.push(baseThemeMapItem.map_name)
                    })
                    lineItem.baseThemes = baseThemes.join(', ')
                })
                setResultList(res.data)
            }
        }
        generateResult()
    }, [selection])

    return (
        <Modal
            wrapperClassName={styles.duplicate_check_modal}
            visible={showModal}
            onCancel={handleCancel}
            title={t('theme.modal.duplicationsTitle')}
        >
            <Loading visible={loading} className={styles.duplicate_check_loading}>
                <Table
                    tableWrapClass={styles.duplicate_check_table_wrap}
                    data={resultList}
                    columns={getDuplicateCheckColumnList(t)}
                    isBottomPagination={false}
                    isDownload={false}
                />
            </Loading>
        </Modal>
    )
}

const DUPLICATED_NAME_EXISTED = '2'

const Footer = ({ t, handleCancel, handleSave }) => {
    return (
        <div className={styles.footer}>
            <Button width="68px" label={t('theme.modal.cancelText')} kind="text" isGray onClick={handleCancel} />
            <Button width="68px" label={t('theme.modal.saveText')} kind="text" onClick={handleSave} />
        </div>
    )
}
export const EditThemeModal = ({
    showModal,
    title,
    id,
    themeName,
    selectedItems,
    handleCancel,
    handleSaveCallback,
    type,
    keyField
}) => {
    const { t } = useTranslation()
    const userInfo = useSelector((state) => state.userInfo)
    const [loading, setLoading] = useState(false)
    const [saveLoading, setSaveLoading] = useState(false)
    const [themeList, setThemeList] = useState([])
    const [themeMapName, setThemeMapName] = useState(themeName || '')
    const [selection, setSelection] = useState(selectedItems || [])

    const [showCheckResultModal, setShowCheckResultModal] = useState(false)

    const [error, setError] = useState({
        thememapname: {
            required: false,
            duplicated: false
        },
        selection: {
            required: false
        }
    })

    const handleSelectError = (tmpSelection) => {
        setError({
            ...error,
            selection: {
                required: !tmpSelection.length
            }
        })
    }

    const handleChange = (val) => {
        setThemeMapName(val)
        setError({
            ...error,
            thememapname: {
                ...error.thememapname,
                required: !val,
                duplicated: false
            }
        })
    }

    const onChangeSelectedData = (selectedData) => {
        setSelection((prevSelection) => {
            if (prevSelection?.length > 0 && selectedData?.length === 0) {
                handleSelectError(selectedData)
            }
            return selectedData
        })
    }

    // fetch data from DB
    const fetchData = () => {
        if (type === BASE_THEME_TYPE) {
            fetchAllThemeData()
        } else {
            fetchAllBaseThemeData()
        }
    }

    const fetchAllThemeData = async () => {
        setLoading(true)
        const res = await fetchAllThemes()
        if (res) {
            setThemeList(res)
        }
        setLoading(false)
    }

    const fetchAllBaseThemeData = async () => {
        setLoading(true)
        const res = await fetchBaseThemes()
        if (res) {
            setThemeList(res.data)
        }
        setLoading(false)
    }

    const doValidation = () => {
        const isInvalid = !themeMapName || !selection.length
        if (isInvalid) {
            setError({
                ...error,
                thememapname: {
                    ...error.thememapname,
                    required: !themeMapName
                },
                selection: {
                    ...error.selection,
                    required: !selection.length
                }
            })
        } else {
            setError({
                thememapname: {
                    required: false,
                    duplicated: false
                },
                selection: {
                    required: false
                }
            })
        }
        return !isInvalid
    }

    const handleSave = () => {
        if (doValidation()) {
            if (type === BASE_THEME_TYPE) {
                saveForBaseTheme()
            } else {
                saveForClientTheme()
            }
        }
    }

    const saveForBaseTheme = async () => {
        const payload = id
            ? {
                  id,
                  themeid: selection,
                  thememaptype: type,
                  thememapname: themeMapName,
                  updateby: userInfo.userinfo.username
              }
            : {
                  themeid: selection,
                  thememaptype: type,
                  thememapname: themeMapName,
                  createby: userInfo.userinfo.username
              }

        setSaveLoading(true)
        const saveResult = id ? await updateBaseThemeMap(payload) : await insertBaseThemeMap(payload)
        setSaveLoading(false)
        if (saveResult) {
            const { hasError, status } = saveResult
            if (!hasError) {
                showPopBanner({
                    message: t(`theme.warningMsg.optSuccess`),
                    status: 'success',
                    duration: 2000
                })
                handleCancel()
                handleSaveCallback && handleSaveCallback()
            } else if (hasError && status === DUPLICATED_NAME_EXISTED) {
                showPopBanner({
                    message: saveResult.desc || t('theme.warningMsg.optError'),
                    status: 'error',
                    duration: 2000
                })
                setError({
                    ...error,
                    thememapname: {
                        ...error.thememapname,
                        duplicated: true
                    }
                })
            }
        }
    }

    const saveForClientTheme = async () => {
        const payload = id
            ? {
                  id,
                  thememapid: selection,
                  thememapname: themeMapName,
                  updateby: userInfo.userinfo.username
              }
            : {
                  thememapid: selection,
                  thememapname: themeMapName,
                  createby: userInfo.userinfo.username
              }
        setSaveLoading(true)
        const saveResult = id ? await updateClientThemeMap(payload) : await insertClientThemeMap(payload)
        setSaveLoading(false)
        if (saveResult) {
            const { hasError, status } = saveResult
            if (!hasError) {
                showPopBanner({
                    message: t(`theme.warningMsg.optSuccess`),
                    status: 'success',
                    duration: 2000
                })
                handleCancel()
                handleSaveCallback && handleSaveCallback()
            } else if (hasError && status === DUPLICATED_NAME_EXISTED) {
                showPopBanner({
                    message: saveResult.desc || t('theme.warningMsg.optError'),
                    status: 'error',
                    duration: 2000
                })
                setError({
                    ...error,
                    thememapname: {
                        ...error.thememapname,
                        duplicated: true
                    }
                })
            }
        }
    }

    useEffect(fetchData, [type])

    return (
        <>
            {saveLoading && <LoadingMask />}
            <Modal
                wrapperClassName={styles.modal}
                visible={showModal}
                title={title}
                onCancel={handleCancel}
                footer={<Footer t={t} handleCancel={handleCancel} handleSave={handleSave} />}
            >
                <div className={styles.option}>
                    <Input
                        label={
                            type === BASE_THEME_TYPE
                                ? t('theme.modal.baseTheme.name')
                                : t('theme.modal.clientTheme.name')
                        }
                        value={themeMapName}
                        width={432}
                        maxlength={100}
                        onChange={handleChange}
                        errorMsg={
                            error.thememapname.required
                                ? t('common.warningMsg.inputRequired')
                                : error.thememapname.duplicated
                                ? t('theme.error.duplicatedNameMsg')
                                : ''
                        }
                    />
                    {type === CLIENT_THEME_TYPE && (
                        <Button
                            width="91px"
                            kind="secondary"
                            size="sm"
                            label={t('theme.modal.duplicationsCheckText')}
                            onClick={() => setShowCheckResultModal(true)}
                        />
                    )}
                </div>
                <Loading visible={loading} className={styles.loading}>
                    <Table
                        tableWrapClass={styles.table_wrap}
                        data={themeList}
                        columns={type === BASE_THEME_TYPE ? getBaseThemeColumnList(t) : getClientThemeColumnList(t)}
                        defaultSelectedData={selectedItems}
                        selectType="checkbox"
                        selectedId={keyField}
                        isAllCheck
                        changeSelectedDataType="row"
                        onChangeSelectedData={onChangeSelectedData}
                        isBottomPagination={false}
                        isDownload={false}
                    />
                </Loading>
                {error.selection.required && (
                    <p className={styles.form_error}>{t('common.warningMsg.selectRequired')}</p>
                )}
            </Modal>
            {showCheckResultModal && (
                <DuplicateCheckResultModal
                    showModal={showCheckResultModal}
                    handleCancel={() => setShowCheckResultModal(false)}
                    selection={selection}
                />
            )}
        </>
    )
}
